import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";

interface UseAttendanceReturn {
  checkedToday: boolean;
  checking: boolean;
  error: string | null;
  streakCount: number;
  doCheckIn: () => Promise<{ success: boolean; message: string }>;
  refreshAttendance: () => Promise<void>;
}

export function useAttendance(): UseAttendanceReturn {
  const { user } = useAuth();
  const [checkedToday, setCheckedToday] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [streakCount, setStreakCount] = useState(0);

  const refreshAttendance = useCallback(async () => {
    if (!user) {
      setCheckedToday(false);
      setStreakCount(0);
      return;
    }

    try {
      const today = new Date().toISOString().split("T")[0];

      // Check today's attendance
      const { data: todayData, error: fetchError } = await supabase
        .from("attendances")
        .select("id")
        .eq("user_id", user.id)
        .eq("check_date", today)
        .maybeSingle();

      if (fetchError) throw fetchError;
      setCheckedToday(!!todayData);

      // Calculate streak: get recent attendance records ordered by date DESC
      const { data: allRecords, error: historyError } = await supabase
        .from("attendances")
        .select("check_date")
        .eq("user_id", user.id)
        .order("check_date", { ascending: false })
        .limit(30);

      if (historyError) throw historyError;

      let streak = 0;
      if (allRecords && allRecords.length > 0) {
        // Start from the most recent check_date
        streak = 1;
        for (let i = 0; i < allRecords.length - 1; i++) {
          const current = new Date(allRecords[i].check_date);
          const next = new Date(allRecords[i + 1].check_date);
          const diffDays = (current.getTime() - next.getTime()) / (1000 * 60 * 60 * 24);
          if (Math.round(diffDays) === 1) {
            streak++;
          } else {
            break;
          }
        }
      }

      setStreakCount(streak);
    } catch {
      setCheckedToday(false);
      setStreakCount(0);
    }
  }, [user]);

  useEffect(() => {
    refreshAttendance();
  }, [refreshAttendance]);

  const doCheckIn = useCallback(async () => {
    if (!user) return { success: false, message: "로그인이 필요합니다" };
    if (checkedToday) return { success: false, message: "오늘은 이미 출석체크를 완료했습니다" };

    setChecking(true);
    setError(null);

    try {
      const today = new Date().toISOString().split("T")[0];

      // Insert attendance record (unique constraint prevents duplicates)
      const { error: insertError } = await supabase
        .from("attendances")
        .insert({
          user_id: user.id,
          check_date: today,
        });

      if (insertError) {
        if (insertError.code === "23505") {
          setCheckedToday(true);
          return { success: false, message: "오늘은 이미 출석체크를 완료했습니다" };
        }
        throw insertError;
      }

      // Calculate new streak (current streak + 1 for today)
      const newStreak = streakCount + 1;
      setStreakCount(newStreak);

      // Base reward: 5P, bonus: 20P if 7-day streak
      const STREAK_BONUS_DAYS = 7;
      const BASE_REWARD = 5;
      const STREAK_BONUS = 20;
      const isStreakBonus = newStreak >= STREAK_BONUS_DAYS && newStreak % STREAK_BONUS_DAYS === 0;
      const totalReward = isStreakBonus ? BASE_REWARD + STREAK_BONUS : BASE_REWARD;

      // Get current points
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("points")
        .eq("id", user.id)
        .maybeSingle();

      if (profileError) throw profileError;

      const currentPoints = profile?.points ?? 0;

      // Update points
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ points: currentPoints + totalReward })
        .eq("id", user.id);

      if (updateError) throw updateError;

      // Record transaction
      const transactionDetail = isStreakBonus
        ? `출석체크 ${newStreak}일 연속 보너스 (+${STREAK_BONUS}P 추가)`
        : "출석체크 보상";

      const { error: txnError } = await supabase
        .from("point_transactions")
        .insert({
          user_id: user.id,
          type: "적립",
          detail: transactionDetail,
          amount: totalReward,
        });

      if (txnError) throw txnError;

      setCheckedToday(true);

      if (isStreakBonus) {
        return {
          success: true,
          message: `${newStreak}일 연속 출석! 기본 ${BASE_REWARD}P + 보너스 ${STREAK_BONUS}P = 총 ${totalReward}P 적립!`,
        };
      }
      return {
        success: true,
        message: `출석체크 완료! ${totalReward}P 적립 (${newStreak}일 연속)`,
      };
    } catch {
      return { success: false, message: "출석체크 중 오류가 발생했습니다" };
    } finally {
      setChecking(false);
    }
  }, [user, checkedToday, streakCount]);

  return {
    checkedToday,
    checking,
    error,
    streakCount,
    doCheckIn,
    refreshAttendance,
  };
}